import { useEffect, useMemo, useState } from 'react';
import { CircleArrowRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { showErrorToast, showSuccessToast } from '@Helpers';

import { sendCustomEvent, sendErrorEvent } from '@Analytics';
import { Badge, ModalComponent, ModalLoading, ModalSuccess } from '@Components';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@Components/ui-v2/combobox';
import { Input } from '@Components/ui-v2/input';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { useMasterUpload } from '@CustomHooks';
import { Options } from '@Models';

interface NewMasterModalProps {
  isOpen: boolean;
  onClose: () => void;
  sources: Options[];
}

const NewMasterModal = ({ isOpen, onClose, sources }: NewMasterModalProps) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [connector, setConnector] = useState<string | null>(null);
  const [masterType, setMasterType] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const { uploadFileToOneDrive } = useMasterUpload();

  const selectedConnector = useMemo(
    () => sources.find((option) => option.value === connector) ?? null,
    [connector, sources],
  );

  const newFileName = useMemo(() => {
    const sanitizedMasterType = masterType?.replace(/[^a-zA-Z0-9\s]/g, '');
    const trimmedMasterType = sanitizedMasterType?.trim() || '';

    const fileId = `${trimmedMasterType}_master_${connector ?? ''}`;
    return fileId.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  }, [connector, masterType]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.target?.files?.[0]) {
      setFile(e.target?.files?.[0] || null);
    }
  };

  useEffect(() => {
    setConnector(null);
    setFile(null);
    setMasterType(null);
    setError(null);
  }, [isOpen]);

  const onSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setError(null);
    if (!connector) {
      setError(t('Configuration.SelectConnectorError'));
      return;
    }
    if (!masterType) {
      setError(t('Configuration.SelectMasterTypeError'));
      return;
    }
    if (!file) {
      setError(t('Configuration.SelectFileError'));
      return;
    }

    const fileId = newFileName.replace(/ /g, '_').toLowerCase();

    setLoading(true);
    uploadFileToOneDrive(file, fileId, newFileName, connector, 'new_master')
      .then(() => {
        onClose();
        setLoading(false);
        setSuccess(true);
        sendCustomEvent('CONFIGURATION_FILE_UPLOADED', {
          type: 'UPLOAD',
          newFileName,
          connector,
        });
        showSuccessToast(t('Configuration.Success'));
      })
      .catch((err) => {
        setLoading(false);
        setSuccess(false);
        sendErrorEvent(`unable to request upload file. ${err}`);
        showErrorToast(err.message || t('Configuration.NewMasterError'));
      });
  };

  return (
    <>
      <ModalComponent isOpen={isOpen} onClose={onClose} title={t('Configuration.ModalTitle')} closeClickOutside={false}>
        <div className="w-full flex items-center flex-col gap-6 max-w-full">
          <div className="w-full">
            <span className="text-gray-900 text-sm" id="connector">
              {`${t('Configuration.Connector')}*`}
            </span>
            <Combobox
              items={sources}
              value={selectedConnector}
              onValueChange={(item: Options | null) => setConnector(item?.value ?? null)}
              itemToStringLabel={(item: Options) => item.label}>
              <ComboboxInput placeholder={t('Configuration.SelectConnector')} />
              <ComboboxContent>
                <ComboboxEmpty>{t('Configuration.SelectConnector')}</ComboboxEmpty>
                <ComboboxList>
                  {(item: Options) => (
                    <ComboboxItem key={item.value} value={item}>
                      {item.label}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </div>

          <div className="w-full">
            <span className="text-gray-900 text-sm" id="masterType">
              {`${t('Configuration.MasterType')}*`}
            </span>
            <div className="flex items-center gap-2 w-full">
              <Input
                type="text"
                placeholder={t('Configuration.SelectMasterType')}
                value={masterType || ''}
                onChange={(e) => setMasterType(e.target.value)}
                className="text-base max-w-[45%]"
              />
              <CircleArrowRightIcon className="size-5 text-brand-500" />
              <Badge variant="green" label={newFileName} className="text-xs" />
            </div>
          </div>

          <div className="flex flex-col gap-1 w-full">
            <label
              htmlFor="file-input"
              className="flex items-center justify-center p-2 rounded-md text-sm font-semibold w-auto border-solid border-gray-300 shadow text-gray-700 hover:border-bg-gray-300 border hover:bg-gray-50 cursor-pointer hover:shadow-sm">
              {t('Configuration.ChooseFile')}

              <input
                id="file-input"
                type="file"
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </label>
            {file && (
              <span className="text-gray-500 text-xs">
                {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </span>
            )}
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex gap-3 items-center w-full justify-center">
            <TrackedButton
              variant="outline"
              buttonName="UPLOAD_CONFIGURATION_FILE_CANCELED"
              onClick={onClose}
              isSubmit={false}
              className="w-full bg-white hover:border-bg-gray-300 hover:bg-gray-50 cursor-pointer hover:shadow-sm">
              <span className="flex items-center justify-center">{t('Configuration.Cancel')}</span>
            </TrackedButton>
            <TrackedButton
              buttonName="UPLOAD_CONFIGURATION_FILE_SUBMIT"
              isSubmit={false}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => onSubmit(e)}
              className="w-full">
              <span className="flex items-center justify-center">{t('Configuration.Upload')}</span>
            </TrackedButton>
          </div>
        </div>
      </ModalComponent>

      <ModalLoading isOpen={loading} onClose={() => setLoading(false)}>
        <div className="text-center -mt-[9px]">{t('Configuration.LoadingModal')}</div>
      </ModalLoading>

      <ModalSuccess isOpen={!!success} onClose={() => setSuccess(false)}>
        <div className="text-center">
          <span className="font-semibold text-lg"> {t('Configuration.SuccessModal')} </span>
        </div>
      </ModalSuccess>
    </>
  );
};

export default NewMasterModal;
